/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Breadcrumbs,
  FormControl,
  IconButton,
  InputAdornment,
  Select,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import SearchIcon from "@mui/icons-material/Search";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CheckIcon from "@mui/icons-material/Check";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { keepPreviousData, useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { NavigableBreadcrumbLink } from "../../../components/page_breadcrumbs/navigable_breadcrumb_link";
import api from "../../../api";
import authenticatedFetch from "../../../api/authenticatedFetch";
import { buildQueryParams } from "../../../utils/buildQueryParams";
import DropdownSearchBar from "../../../components/dorpdown_searchbar";
import { DropdownInfiniteLoader } from "../../../components/dropdown_infinite_loader";
import { IndexTable } from "../../../components/index_table";
import { Loader } from "../../../components/loader";
import { MenuHeading } from "../../../components/menu_heading";
import { ResetButton } from "../../../components/reset_button";
import { SelectedMenuItem } from "../../../components/selected_menu_item";
import { StyledMenuItem } from "../../../components/styled_menu_item";
import { UpdateButton } from "../../../components/update_button";
import { OrderNumberCell } from "../../../components/order_number_cell";
import { OrderAppliedDiscountModal } from "../../../components/order_discount_modal";
import { Query_Keys } from "../../../lib/queryKeys";
import { useNotificationContext } from "../../../contexts/notificationContext";
import { PerPage } from "../../../constants/perPage";
import { formatDate } from "../../../utils/formatDate";
import { hasSingleItem } from "../../../utils/hasSingleItem";
import { getSingleItemValue } from "../../../utils/getSingleItemValue";
import { useDebounce } from "../../../utils/useDebounce";
import {
  getDropdownNextPageParam,
  mergeOptionsIfChanged,
  mergeUniqueByKey,
  normalizeRestaurant,
  normalizeRestaurantGroup,
} from "../../../utils/dropdownInfiniteQuery";
import { useInfiniteScrollObserver } from "../../../hooks/useInfiniteScrollObserver";
import {
  ALL_FILTER_VALUE,
  DEFAULT_SORT_BY,
  DEFAULT_SORT_ORDER,
  DISCOUNT_NAME_STATUS_HINTS,
  DISCOUNT_NAME_STATUS_LABELS,
  PERQ_CHECK_IN_OPTIONS,
  PERQ_DISCOUNT_CLASSES,
  PERQ_DISCOUNT_CLASS_LABELS,
  PERQ_ORDER_TYPES,
  PERQ_REPORT_DATE_FILTERS,
  PERQ_SUMMARY_CLICKABLE_CARDS,
  defaultPerqFilters,
  getDiscountNameOptionParams,
  getDiscountNamesDisplayValue,
  getPerqReportApiFilters,
  getFiltersForSummaryCard,
  getSelectedSummaryCard,
  isTipCompliant,
  formatMoney,
  formatPercent,
} from "../../../utils/perqReportDrilldown";
import { PerqReportSummary } from "./summary";
import { PerqReportBreakdown } from "./breakdown";
import {
  StyledBreadCrumbContainer,
  StyledLink,
  filterContainerStyles,
  selectBaseStyles,
  viewTypographyStyles,
  datePickerStyles,
  dropdownMenuProps,
  plainMenuProps,
  OwnersTableBadge,
  orderNoCellStyles,
  orderNoBadgeSlotStyles,
  CheckInCodePill,
  BucketPill,
  bucketPillContentStyles,
  bucketDotStyles,
  bucketCountStyles,
  bucketTooltipStyles,
  bucketTooltipHeaderStyles,
  bucketTooltipBodyStyles,
  bucketTooltipRowStyles,
  WarningFlagChip,
  ValidFlagChip,
  FlagsCellContainer,
  StyledSearchBar,
  complianceIndicatorStyles,
  grossProfitStyles,
} from "./styles";
import { isValidDayjsValue } from "../../../utils/isValidDayjsValue";
import { isValidCustomDateRange } from "../../../utils/isValidCustomDateRange";

const CHECK_IN_LABELS = {
  with: "With Check-in",
  without: "Without Check-in",
};

const ORDER_TYPE_LABELS = {
  all: "All orders",
  ot: "OwnersTable",
  non_ot: "Non-OwnersTable",
};

const EMPLOYEE_NAME_FALLBACK = "Name not available";

const normalizeDayjsValue = (value) => {
  if (!value) return null;
  if (typeof value?.isValid === "function" && typeof value?.format === "function") {
    return value;
  }
  const parsedValue = dayjs(value?.$d || value);
  return parsedValue.isValid() ? parsedValue : null;
};

// Test id for each bucket's tooltip.
const BUCKET_TOOLTIP_TEST_IDS = {
  managed: "managed-perq-tooltip",
  unmanaged: "unmanaged-discount-tooltip",
  unclassified: "unclassified-discount-tooltip",
};

// Row test id from the order's database id.
const getPerqRowProps = (row) => (row?.id ? { "data-testid": `perq-row-${row.id}` } : {});

const BucketCell = ({ bucket, bucketType }) => {
  const count = bucket?.count || 0;
  const items = Array.isArray(bucket?.items) ? bucket.items : [];

  if (!count) {
    return <span style={{ color: "#c2c2c2" }}>—</span>;
  }

  const breakdown = items.length ? (
    <Box sx={{ minWidth: 200 }}>
      <Box sx={bucketTooltipHeaderStyles(bucketType)}>
        <span className="bucket-tooltip-title">
          <span className="bucket-tooltip-dot" />
          {PERQ_DISCOUNT_CLASS_LABELS[bucketType] || bucketType}
        </span>
        <span className="bucket-tooltip-total">{formatMoney(bucket?.amount)}</span>
      </Box>
      <Box sx={bucketTooltipBodyStyles}>
        {items.map((item, index) => (
          <Box key={`${item?.name}-${index}`} sx={bucketTooltipRowStyles}>
            <span className="bucket-tooltip-item-name">{item?.name || "Unknown"}</span>
            <span className="bucket-tooltip-item-amount">{formatMoney(item?.amount)}</span>
          </Box>
        ))}
      </Box>
    </Box>
  ) : (
    ""
  );

  return (
    <Tooltip
      arrow
      placement="bottom"
      title={breakdown}
      componentsProps={{
        tooltip: { sx: bucketTooltipStyles, "data-testid": BUCKET_TOOLTIP_TEST_IDS[bucketType] },
      }}
    >
      <BucketPill
        bucketType={bucketType}
        data-testid={`perq-bucket-${bucketType}`}
        label={
          <Box component="span" sx={bucketPillContentStyles}>
            <Box component="span" sx={bucketDotStyles(bucketType)} />
            <span>{formatMoney(bucket?.amount)}</span>
            {count > 1 ? (
              <Box component="span" sx={bucketCountStyles}>{`×${count}`}</Box>
            ) : null}
          </Box>
        }
      />
    </Tooltip>
  );
};

const ComplianceCell = ({ tipPercent, thresholdPercent }) => {
  const compliant = isTipCompliant(tipPercent, thresholdPercent);

  return (
    <Box
      data-testid={`perq-compliance-${compliant ? "yes" : "no"}`}
      sx={complianceIndicatorStyles(compliant)}
    >
      {compliant ? (
        <CheckIcon sx={{ color: "white", fontSize: "1.2rem" }} />
      ) : (
        <ClearIcon sx={{ color: "white", fontSize: "1.2rem" }} />
      )}
    </Box>
  );
};

const GrossProfitCell = ({ value }) => {
  const numericValue = Number(value);
  const unavailable =
    value === null || value === undefined || value === "" || Number.isNaN(numericValue);

  if (unavailable) {
    return (
      <span data-testid="perq-gross-profit-unavailable" style={{ color: "#c2c2c2" }}>
        —
      </span>
    );
  }

  const negative = numericValue < 0;

  return (
    <Box
      component="span"
      data-testid={`perq-gross-profit-${negative ? "negative" : "non-negative"}`}
      sx={grossProfitStyles(negative)}
    >
      {negative
        ? `-${formatMoney(Math.abs(numericValue))}`
        : formatMoney(numericValue)}
    </Box>
  );
};

const FlagsCell = ({ flags }) => {
  if (!flags) return <span style={{ color: "#c2c2c2" }}>—</span>;

  if (flags.valid) {
    return (
      <FlagsCellContainer>
        <ValidFlagChip icon={<CheckCircleOutlineIcon />} label="Valid" />
      </FlagsCellContainer>
    );
  }

  return (
    <FlagsCellContainer>
      {flags.no_check_in ? (
        <WarningFlagChip icon={<WarningAmberIcon />} label="No check-in" />
      ) : null}
      {flags.low_tip ? (
        <WarningFlagChip icon={<WarningAmberIcon />} label="Low tip" />
      ) : null}
    </FlagsCellContainer>
  );
};

export const PerqReport = () => {
  const showNotification = useNotificationContext();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [filters, setFilters] = useState(defaultPerqFilters());
  const [appliedFilters, setAppliedFilters] = useState(defaultPerqFilters());
  const [filtersChanged, setFiltersChanged] = useState(false);

  const [restaurantGroupSearch, setRestaurantGroupSearch] = useState("");
  const [restaurantSubGroupSearch, setRestaurantSubGroupSearch] = useState("");
  const [restaurantSearch, setRestaurantSearch] = useState("");
  const [restaurantGroupOptionsCache, setRestaurantGroupOptionsCache] = useState([]);
  const [restaurantSubGroupOptionsCache, setRestaurantSubGroupOptionsCache] = useState([]);
  const [restaurantOptionsCache, setRestaurantOptionsCache] = useState([]);
  const [discountNameSearch, setDiscountNameSearch] = useState("");
  const [discountNameMenuOpen, setDiscountNameMenuOpen] = useState(false);
  // Badge status of each selected name, taken from the option it was picked from.
  const [selectedDiscountNameStatuses, setSelectedDiscountNameStatuses] = useState(new Map());

  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState(DEFAULT_SORT_BY);
  const [sortOrder, setSortOrder] = useState(DEFAULT_SORT_ORDER);

  const [orderViewOpen, setOrderViewOpen] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [csvLoading, setCsvLoading] = useState(false);

  const debouncedRestaurantGroupSearch = useDebounce(restaurantGroupSearch.trim(), 300);
  const debouncedRestaurantSubGroupSearch = useDebounce(restaurantSubGroupSearch.trim(), 300);
  const debouncedRestaurantSearch = useDebounce(restaurantSearch.trim(), 300);
  const debouncedDiscountNameSearch = useDebounce(discountNameSearch.trim(), 300);
  const debouncedSearchTerm = useDebounce(filters.search.trim(), 300);

  const hasGroupSelected = filters.restaurant_group_id !== ALL_FILTER_VALUE;
  const hasSubGroupSelected = filters.restaurant_sub_group_id !== ALL_FILTER_VALUE;
  const hasCheckInSelected = filters.check_in !== ALL_FILTER_VALUE;

  const fetchRestaurantGroups = async ({ pageParam = 1 }) => {
    const response = await api.get("/v1/admin/dropdowns/restaurant_groups_list", {
      params: {
        page: pageParam,
        per_page: PerPage,
        search: debouncedRestaurantGroupSearch || undefined,
      },
    });
    return response?.data?.map(normalizeRestaurantGroup) || [];
  };

  const {
    data: restaurantGroups = [],
    isFetching: restaurantGroupFetching,
    isFetchingNextPage: restaurantGroupsFetchingNextPage,
    fetchNextPage: fetchNextRestaurantGroupsPage,
    hasNextPage: hasNextRestaurantGroupsPage,
  } = useInfiniteQuery({
    queryKey: Query_Keys.restaurant_groups_dropdown({
      search: debouncedRestaurantGroupSearch,
      source: "perq_report",
    }),
    queryFn: fetchRestaurantGroups,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: (data) => data.pages.flat(),
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  const { rootRef: restaurantGroupMenuRef, targetRef: restaurantGroupLoaderRef } =
    useInfiniteScrollObserver({
      fetchNextPage: fetchNextRestaurantGroupsPage,
      hasNextPage: hasNextRestaurantGroupsPage,
      isFetchingNextPage: restaurantGroupsFetchingNextPage,
    });

  const fetchRestaurantSubGroups = async ({ pageParam = 1 }) => {
    if (!hasGroupSelected) return [];
    const response = await api.get("/v1/admin/dropdowns/restaurant_sub_groups_list", {
      params: {
        restaurant_group_id: filters.restaurant_group_id,
        page: pageParam,
        per_page: PerPage,
        search: debouncedRestaurantSubGroupSearch || undefined,
      },
    });
    return response?.data || [];
  };

  const {
    data: restaurantSubGroups = [],
    isFetching: restaurantSubGroupFetching,
    isFetchingNextPage: restaurantSubGroupsFetchingNextPage,
    fetchNextPage: fetchNextRestaurantSubGroupsPage,
    hasNextPage: hasNextRestaurantSubGroupsPage,
  } = useInfiniteQuery({
    queryKey: Query_Keys.restaurant_sub_groups_dropdown({
      restaurant_group_id: filters.restaurant_group_id,
      search: debouncedRestaurantSubGroupSearch,
      source: "perq_report",
    }),
    queryFn: fetchRestaurantSubGroups,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: (data) => data.pages.flat(),
    enabled: hasGroupSelected,
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  const { rootRef: restaurantSubGroupMenuRef, targetRef: restaurantSubGroupLoaderRef } =
    useInfiniteScrollObserver({
      fetchNextPage: fetchNextRestaurantSubGroupsPage,
      hasNextPage: hasNextRestaurantSubGroupsPage,
      isFetchingNextPage: restaurantSubGroupsFetchingNextPage,
    });

  const fetchRestaurants = async ({ pageParam = 1 }) => {
    const response = await api.get("/v1/admin/dropdowns/restaurants_list", {
      params: {
        page: pageParam,
        per_page: PerPage,
        search: debouncedRestaurantSearch || undefined,
        restaurant_group_ids: hasGroupSelected ? filters.restaurant_group_id : undefined,
        restaurant_sub_group_id: hasSubGroupSelected ? filters.restaurant_sub_group_id : undefined,
      },
    });
    return (
      response?.data?.map((record) => ({
        ...normalizeRestaurant(record),
        id: Number(record?.value ?? record?.id),
      })) || []
    );
  };

  const {
    data: restaurants = [],
    isFetching: restaurantFetching,
    isFetchingNextPage: restaurantsFetchingNextPage,
    fetchNextPage: fetchNextRestaurantsPage,
    hasNextPage: hasNextRestaurantsPage,
  } = useInfiniteQuery({
    queryKey: Query_Keys.restaurants_dropdown({
      restaurant_group_id: filters.restaurant_group_id,
      restaurant_sub_group_id: filters.restaurant_sub_group_id,
      search: debouncedRestaurantSearch,
      source: "perq_report",
    }),
    queryFn: fetchRestaurants,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: (data) => data.pages.flat(),
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  const { rootRef: restaurantMenuRef, targetRef: restaurantLoaderRef } =
    useInfiniteScrollObserver({
      fetchNextPage: fetchNextRestaurantsPage,
      hasNextPage: hasNextRestaurantsPage,
      isFetchingNextPage: restaurantsFetchingNextPage,
    });

  const discountNameOptionParams = getDiscountNameOptionParams(filters);

  const fetchDiscountNames = async ({ pageParam = 1 }) => {
    const response = await api.get("/v1/admin/dropdowns/configured_discounts_list", {
      params: {
        ...discountNameOptionParams,
        page: pageParam,
        per_page: PerPage,
        search: debouncedDiscountNameSearch || undefined,
      },
    });
    return response?.data || [];
  };

  // Fetched only while the menu is open, so scope changes don't refetch unseen options.
  const {
    data: discountNames = [],
    isFetching: discountNameFetching,
    isFetchingNextPage: discountNamesFetchingNextPage,
    fetchNextPage: fetchNextDiscountNamesPage,
    hasNextPage: hasNextDiscountNamesPage,
  } = useInfiniteQuery({
    queryKey: Query_Keys.configured_discounts_dropdown({
      ...discountNameOptionParams,
      search: debouncedDiscountNameSearch,
      source: "perq_report",
    }),
    queryFn: fetchDiscountNames,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: (data) => data.pages.flat(),
    enabled: discountNameMenuOpen,
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  const { rootRef: discountNameMenuRef, targetRef: discountNameLoaderRef } =
    useInfiniteScrollObserver({
      fetchNextPage: fetchNextDiscountNamesPage,
      hasNextPage: hasNextDiscountNamesPage,
      isFetchingNextPage: discountNamesFetchingNextPage,
    });

  const fetchDiscountReports = async () => {
    const response = await api.get("/v1/admin/discount_reports", {
      params: {
        page,
        per_page: itemsPerPage,
        sort_by: sortBy,
        sort_order: sortOrder,
        ...getPerqReportApiFilters(appliedFilters),
      },
    });

    return {
      data: response?.data || [],
      pageCount: Number(response?.headers?.["total-count"] || 0),
    };
  };

  const {
    data: discountReports,
    isFetching,
    error,
  } = useQuery({
    queryKey: Query_Keys.discount_reports({
      queryFilter: appliedFilters,
      page,
      itemsPerPage,
      sortBy,
      sortOrder,
    }),
    queryFn: fetchDiscountReports,
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  const fetchDiscountReportSummary = async () => {
    const response = await api.get("/v1/admin/discount_reports/summary", {
      params: getPerqReportApiFilters(appliedFilters),
    });
    return response?.data || {};
  };

  const {
    data: summaryData,
    isFetching: summaryFetching,
    error: summaryError,
  } = useQuery({
    queryKey: Query_Keys.discount_report_summary({ queryFilter: appliedFilters }),
    queryFn: fetchDiscountReportSummary,
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  const fetchOrderData = async () => {
    const response = await api.get(`/v1/admin/orders/${selectedOrderId}/detail`);
    return response?.data;
  };

  const { data: orderData, isLoading: orderDataLoading } = useQuery({
    queryKey: Query_Keys.applied_discount({ selected_order: selectedOrderId }),
    queryFn: fetchOrderData,
    enabled: !!selectedOrderId,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    setRestaurantGroupOptionsCache((current) =>
      mergeOptionsIfChanged(current, restaurantGroups, "label", ["value"]),
    );
  }, [restaurantGroups]);

  useEffect(() => {
    setRestaurantSubGroupOptionsCache((current) =>
      mergeOptionsIfChanged(current, restaurantSubGroups, "value", ["label"]),
    );
  }, [restaurantSubGroups]);

  useEffect(() => {
    setRestaurantOptionsCache((current) =>
      mergeOptionsIfChanged(current, restaurants, "id", ["name"]),
    );
  }, [restaurants]);

  useEffect(() => {
    if (debouncedRestaurantGroupSearch) return;
    if (hasNextRestaurantGroupsPage) return;
    if (filters.restaurant_group_id !== ALL_FILTER_VALUE) return;
    if (!hasSingleItem(restaurantGroups)) return;

    const singleGroupId = getSingleItemValue(restaurantGroups)?.label;
    setFilters((prev) => ({ ...prev, restaurant_group_id: singleGroupId }));
  }, [debouncedRestaurantGroupSearch, hasNextRestaurantGroupsPage, restaurantGroups]);

  useEffect(() => {
    if (debouncedRestaurantSearch) return;
    if (hasNextRestaurantsPage) return;
    if (!filters.restaurant_ids.every((id) => id === ALL_FILTER_VALUE)) return;
    if (!hasSingleItem(restaurants)) return;

    const singleRestaurantId = getSingleItemValue(restaurants)?.id;
    setFilters((prev) => ({ ...prev, restaurant_ids: [singleRestaurantId] }));
  }, [debouncedRestaurantSearch, hasNextRestaurantsPage, restaurants]);

  useEffect(() => {
    const failed = error || summaryError;
    if (!failed) return;
    const message = failed?.response?.data?.error || "Failed to load perq report.";
    showNotification(message, "error");
  }, [error, summaryError, showNotification]);

  const selectedRestaurantGroupOption = restaurantGroupOptionsCache?.find(
    (group) => `${group?.label}` === `${filters.restaurant_group_id}`,
  );
  const displayedRestaurantGroups = restaurantGroupSearch.trim()
    ? restaurantGroups
    : selectedRestaurantGroupOption
      ? mergeUniqueByKey(restaurantGroups, [selectedRestaurantGroupOption], "label")
      : restaurantGroups;

  const selectedSubGroupOption = restaurantSubGroupOptionsCache?.find(
    (subGroup) => `${subGroup?.value}` === `${filters.restaurant_sub_group_id}`,
  );
  const displayedSubGroups = restaurantSubGroupSearch.trim()
    ? restaurantSubGroups
    : selectedSubGroupOption
      ? mergeUniqueByKey(restaurantSubGroups, [selectedSubGroupOption], "value")
      : restaurantSubGroups;

  const selectedRestaurantOptions = restaurantOptionsCache?.filter((restaurant) =>
    filters.restaurant_ids?.some((id) => `${id}` === `${restaurant?.id}`),
  );
  const displayedRestaurants = restaurantSearch.trim()
    ? restaurants
    : mergeUniqueByKey(restaurants, selectedRestaurantOptions, "id");

  // Name with its Active / Inactive / Both / No Perq badge, when the option has one.
  const renderDiscountNameOption = (option) => (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, width: "100%" }}>
      <span>{option.label}</span>
      {DISCOUNT_NAME_STATUS_LABELS[option.status] && (
        <Typography
          component="span"
          variant="caption"
          title={DISCOUNT_NAME_STATUS_HINTS[option.status]}
          data-testid="discount-name-status"
          sx={{ color: "text.secondary", whiteSpace: "nowrap" }}
        >
          {DISCOUNT_NAME_STATUS_LABELS[option.status]}
        </Typography>
      )}
    </Box>
  );

  // Selected names stay listed, with their badge, even when they are not on a loaded page.
  const loadedDiscountNameStatuses = new Map(discountNames.map((option) => [option.value, option.status]));
  const displayedDiscountNames = discountNameSearch.trim()
    ? discountNames
    : [
        ...discountNames,
        ...filters.discount_names
          .filter((name) => !loadedDiscountNameStatuses.has(name))
          .map((name) => ({ label: name, value: name, status: selectedDiscountNameStatuses.get(name) })),
      ];

  const getRestaurantGroupName = (id) => {
    if (id === ALL_FILTER_VALUE) return "All restaurant groups";
    const cached = restaurantGroupOptionsCache?.find((group) => `${group?.label}` === `${id}`);
    if (cached?.value) return cached.value;
    const group = restaurantGroups?.find((record) => `${record.label}` === `${id}`);
    return group?.value || "All restaurant groups";
  };

  const getSubGroupName = (id) => {
    if (id === ALL_FILTER_VALUE) return "All sub-groups";
    const cached = restaurantSubGroupOptionsCache?.find((sg) => `${sg?.value}` === `${id}`);
    if (cached?.label) return cached.label;
    const subGroup = restaurantSubGroups?.find((record) => `${record.value}` === `${id}`);
    return subGroup?.label || "All sub-groups";
  };

  const getRestaurantDisplayValue = () => {
    if (!filters.restaurant_ids?.length || filters.restaurant_ids.includes(ALL_FILTER_VALUE)) {
      return "All restaurants";
    }
    if (filters.restaurant_ids.length === 1) {
      const restaurant = restaurantOptionsCache?.find(
        (record) => `${record.id}` === `${filters.restaurant_ids[0]}`,
      );
      return restaurant?.name || "All restaurants";
    }
    return `${filters.restaurant_ids.length} Restaurants Selected`;
  };

  const getDiscountsDisplayValue = () => {
    if (!filters.discounts?.length) return "All discounts";
    if (filters.discounts.length === 1) return PERQ_DISCOUNT_CLASS_LABELS[filters.discounts[0]];
    return `${filters.discounts.length} discount types`;
  };

  const markChanged = () => setFiltersChanged(true);

  const handleDateFilterChange = (event) => {
    const nextDateFilter = event.target.value;
    setFilters((prev) => ({
      ...prev,
      dateFilter: nextDateFilter,
      customDateFrom: nextDateFilter === "Custom Date" ? null : prev.customDateFrom,
      customDateTo: nextDateFilter === "Custom Date" ? null : prev.customDateTo,
    }));
    markChanged();
  };

  const handleDateChange = (field, value) => {
    setFilters((prev) => {
      if (field === "dateFilter") {
        return {
          ...prev,
          dateFilter: value,
          customDateFrom: null,
          customDateTo: null,
        };
      }

      if (field === "customDateFrom") {
        if (!value) {
          return {
            ...prev,
            customDateFrom: null,
            customDateTo: null,
          };
        }

        return {
          ...prev,
          customDateFrom: value,
          customDateTo:
            isValidDayjsValue(value) && !isValidDayjsValue(prev.customDateTo)
              ? dayjs().startOf("day")
              : prev.customDateTo,
        };
      }

      return { ...prev, [field]: normalizeDayjsValue(value) };
    });
    markChanged();
  };

  const handleRestaurantGroupChange = (event) => {
    setRestaurantSubGroupSearch("");
    setRestaurantSearch("");
    setFilters((prev) => ({
      ...prev,
      restaurant_group_id: event.target.value,
      restaurant_sub_group_id: ALL_FILTER_VALUE,
      restaurant_ids: [ALL_FILTER_VALUE],
      discount_names: [],
    }));
    markChanged();
  };

  const handleRestaurantGroupClear = () => {
    setRestaurantSubGroupSearch("");
    setRestaurantSearch("");
    setFilters((prev) => ({
      ...prev,
      restaurant_group_id: ALL_FILTER_VALUE,
      restaurant_sub_group_id: ALL_FILTER_VALUE,
      restaurant_ids: [ALL_FILTER_VALUE],
      discount_names: [],
    }));
    markChanged();
  };

  const handleSubGroupChange = (event) => {
    setRestaurantSearch("");
    setFilters((prev) => ({
      ...prev,
      restaurant_sub_group_id: event.target.value,
      restaurant_ids: [ALL_FILTER_VALUE],
      discount_names: [],
    }));
    markChanged();
  };

  const handleSubGroupClear = () => {
    setRestaurantSearch("");
    setFilters((prev) => ({
      ...prev,
      restaurant_sub_group_id: ALL_FILTER_VALUE,
      restaurant_ids: [ALL_FILTER_VALUE],
      discount_names: [],
    }));
    markChanged();
  };

  const handleRestaurantChange = (event) => {
    const { value } = event.target;
    if ((value.length === 1 && value.includes(ALL_FILTER_VALUE)) || value.length === 0) {
      setFilters((prev) => ({ ...prev, restaurant_ids: [ALL_FILTER_VALUE], discount_names: [] }));
    } else {
      setFilters((prev) => ({
        ...prev,
        restaurant_ids: value.filter((record) => record !== ALL_FILTER_VALUE),
        discount_names: [],
      }));
    }
    markChanged();
  };

  const handleRestaurantClear = () => {
    setFilters((prev) => ({ ...prev, restaurant_ids: [ALL_FILTER_VALUE], discount_names: [] }));
    markChanged();
  };

  const handleDiscountsChange = (event) => {
    const { value } = event.target;
    setFilters((prev) => ({
      ...prev,
      discounts: (Array.isArray(value) ? value : []).filter((item) =>
        PERQ_DISCOUNT_CLASSES.includes(item),
      ),
      discount_names: [],
    }));
    markChanged();
  };

  const handleDiscountNamesChange = (event) => {
    const { value } = event.target;
    const names = (Array.isArray(value) ? value : []).filter((item) => typeof item === "string");
    // Kept for the selected names only, so it never grows past the selection.
    setSelectedDiscountNameStatuses(
      (prev) =>
        new Map(
          names.map((name) => [
            name,
            loadedDiscountNameStatuses.has(name) ? loadedDiscountNameStatuses.get(name) : prev.get(name),
          ]),
        ),
    );
    setFilters((prev) => ({ ...prev, discount_names: names }));
    markChanged();
  };

  const handleDiscountNamesClear = () => {
    setFilters((prev) => ({ ...prev, discount_names: [] }));
    markChanged();
  };

  const handleCheckInChange = (event) => {
    setFilters((prev) => ({
      ...prev,
      check_in: event.target.value,
    }));
    markChanged();
  };

  const handleCheckInClear = () => {
    setFilters((prev) => ({
      ...prev,
      check_in: ALL_FILTER_VALUE,
    }));
    markChanged();
  };

  const handleOrderTypeChange = (event) => {
    setFilters((prev) => ({ ...prev, order_type: event.target.value }));
    markChanged();
  };

  const handleColumnSortChange = (nextSortBy, nextSortOrder) => {
    setSortBy(nextSortBy);
    setSortOrder(nextSortOrder);
    setPage(1);
  };

  const handleSearchChange = (event) => {
    setFilters((prev) => ({ ...prev, search: event.target.value }));
  };

  const isCustomDateRangeValid =
    filters?.dateFilter !== "Custom Date" ||
    isValidCustomDateRange(filters?.customDateFrom, filters?.customDateTo);

  const isUpdateDisabled =
    filters?.dateFilter === "Custom Date" &&
    !isCustomDateRangeValid;

  const handleApplyFilters = () => {
    setAppliedFilters(filters);
    setFiltersChanged(false);
    setPage(1);
  };

  const handleReset = () => {
    const resetFilters = defaultPerqFilters();
    setFilters(resetFilters);
    setAppliedFilters(resetFilters);
    setFiltersChanged(false);
    setPage(1);
    setItemsPerPage(25);
    setSortBy(DEFAULT_SORT_BY);
    setSortOrder(DEFAULT_SORT_ORDER);
    setRestaurantGroupSearch("");
    setRestaurantSubGroupSearch("");
    setRestaurantSearch("");
    setDiscountNameSearch("");
  };

  const handleSummaryCardClick = useCallback(
    (cardKey) => {
      if (!PERQ_SUMMARY_CLICKABLE_CARDS.includes(cardKey)) return;
      const nextFilters = getFiltersForSummaryCard(appliedFilters, cardKey);
      setFilters(nextFilters);
      setAppliedFilters(nextFilters);
      setFiltersChanged(false);
      setPage(1);
    },
    [appliedFilters],
  );

  const selectedSummaryCard = getSelectedSummaryCard(appliedFilters);

  useEffect(() => {
    if (appliedFilters.search === debouncedSearchTerm) return;

    setAppliedFilters((prev) => ({ ...prev, search: debouncedSearchTerm }));
    setPage(1);
  }, [debouncedSearchTerm]);

  const handleOpen = (orderId) => {
    setSelectedOrderId(orderId);
    setOrderViewOpen(true);
  };

  const handleCloseOrderModal = () => {
    setOrderViewOpen(false);
    setSelectedOrderId(null);
  };

  const handleExportCsv = async () => {
    try {
      setCsvLoading(true);

      const query = buildQueryParams({
        report: "discount_report",
        sort_by: sortBy,
        sort_order: sortOrder,
        ...getPerqReportApiFilters(appliedFilters),
      });

      const { response } = await authenticatedFetch(
        `${process.env.REACT_APP_API_BASE_URL}/v1/admin/csv_reports/export?${query}`,
        {
          method: "GET",
          defaultErrorMessage: "Failed to download CSV",
        },
      );

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const date = dayjs().format("YYYY-MM-DD");

      link.href = downloadUrl;
      link.download = `discount_report_${date}.csv`;
      link.click();

      window.URL.revokeObjectURL(downloadUrl);
      showNotification("CSV downloaded successfully", "success");
    } catch (error) {
      showNotification(error?.message || "Failed to download CSV", "error");
    } finally {
      setCsvLoading(false);
    }
  };

  const columns = [
    { key: "date", label: "Order Date", width: 170 },
    { key: "order_no", label: "Order", width: 130 },
    { key: "view", label: "View", sortable: false, width: 70 },
    { key: "restaurant", label: "Restaurant", width: 150 },
    { key: "employee", label: "Employee Name", width: 150 },
    { key: "check_in_code", label: "Check-in code", width: 130 },
    { key: "managed", label: "Managed perq", sortable: false, width: 150 },
    { key: "unmanaged", label: "Unmanaged discount", sortable: false, width: 170 },
    { key: "unclassified", label: "Unclassified discount", sortable: false, width: 180 },
    { key: "discount_total", label: "Perq Amount", width: 130 },
    { key: "subtotal", label: "Amount", width: 110 },
    { key: "tax", label: "Tax", width: 110 },
    { key: "tip", label: "Tip", width: 110 },
    { key: "gratuity", label: "Gratuity", width: 110 },
    { key: "total", label: "Total", width: 110 },
    {
      key: "total_discount_percent",
      label: "Total Perq %",
      sortable: false,
      width: 130,
    },
    { key: "gross_profit", label: "Gross Profit", sortable: false, width: 130 },
    { key: "tip_percent", label: "Tip %", sortable: false, width: 90 },
    { key: "compliance_threshold_percent", label: "Compliance Threshold %", sortable: false, width: 100 },
    { key: "compliance", label: "Compliance", sortable: false, width: 110 },
    { key: "flags", label: "Flags", sortable: false, width: 200 },
  ];

  const rows = useMemo(
    () =>
      (discountReports?.data || []).map((record) => ({
        id: record?.id,
        date: {
          display: formatDate(record?.opened, record?.time_zone_abbr),
          sortValue: record?.opened,
        },
        order_no: {
          display: (
            <Box sx={orderNoCellStyles}>
              <Box sx={orderNoBadgeSlotStyles}>
                {record?.has_ot_discount ? (
                  <OwnersTableBadge label="OT" size="small" />
                ) : null}
              </Box>
              <span>{record?.order_no || "-"}</span>
            </Box>
          ),
          sortValue: record?.order_no,
        },
        view: (
          <OrderNumberCell orderValue={record?.order_id} onView={handleOpen} />
        ),
        restaurant: record?.restaurant || "-",
        employee: record?.network_guest_name || EMPLOYEE_NAME_FALLBACK,
        check_in_code: record?.check_in_code ? (
          <CheckInCodePill label={record.check_in_code} size="small" />
        ) : (
          <span style={{ color: "#c2c2c2" }}>—</span>
        ),
        subtotal: formatMoney(record?.subtotal),
        discount_total: formatMoney(record?.discount_total),
        tax: formatMoney(record?.tax),
        tip: formatMoney(record?.tip),
        gratuity: formatMoney(record?.gratuity),
        total: formatMoney(record?.total),
        managed: <BucketCell bucket={record?.managed} bucketType="managed" />,
        unmanaged: <BucketCell bucket={record?.unmanaged} bucketType="unmanaged" />,
        unclassified: (
          <BucketCell bucket={record?.unclassified} bucketType="unclassified" />
        ),
        total_discount_percent: formatPercent(record?.total_discount_percent),
        gross_profit: <GrossProfitCell value={record?.gross_profit} />,
        compliance_threshold_percent: formatPercent(record?.compliance_threshold_percent),
        tip_percent: (
          <span>
            {formatPercent(record?.tip_percent)}
          </span>
        ),
        compliance: (
          <ComplianceCell
            tipPercent={record?.tip_percent}
            thresholdPercent={record?.compliance_threshold_percent}
          />
        ),
        flags: <FlagsCell flags={record?.flags} />,
      })),
    [discountReports?.data],
  );

  // Scoped to the table region only, including the first load, so breadcrumbs, filters
  // and the summary cards stay mounted while data is fetched. Matches Tip Compliance
  // and Employee Discounts.
  const showTableLoader = isFetching;

  return (
    <React.Fragment>
      <Box
        sx={{ height: "100%", width: "100%", overflowY: "scroll", overflowX: "hidden" }}
      >
        <StyledBreadCrumbContainer>
          <Breadcrumbs>
            <NavigableBreadcrumbLink LinkComponent={StyledLink} label="Home" to="/admin/dashboard" />
            <StyledLink color="#515152">Reports</StyledLink>
            <StyledLink color="#515152">Sales</StyledLink>
            <StyledLink color="#515152">Perq Report</StyledLink>
          </Breadcrumbs>
        </StyledBreadCrumbContainer>

        <Box sx={filterContainerStyles}>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", lg: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", lg: "center" },
              gap: { xs: 2, lg: 1 },
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: { xs: 2, sm: "12px" },
                flexWrap: "wrap",
                alignItems: { xs: "flex-start", sm: "center" },
                flex: 1,
              }}
            >
              <Typography variant="body2" sx={viewTypographyStyles}>
                View
              </Typography>

              <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: "150px" } }}>
                <Select
                  data-testid="perq-date-filter"
                  value={filters.dateFilter}
                  renderValue={() => filters.dateFilter}
                  onChange={handleDateFilterChange}
                  sx={selectBaseStyles}
                  MenuProps={plainMenuProps}
                >
                  {PERQ_REPORT_DATE_FILTERS.map((value) => (
                    <StyledMenuItem key={value} value={value}>
                      {filters.dateFilter === value ? (
                        <SelectedMenuItem data={value} />
                      ) : (
                        value
                      )}
                    </StyledMenuItem>
                  ))}
                </Select>
              </FormControl>

              {filters.dateFilter === "Custom Date" ? (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
                  <Typography variant="body2" sx={viewTypographyStyles}>
                    from
                  </Typography>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      format="MM-DD-YYYY"
                      value={normalizeDayjsValue(filters.customDateFrom)}
                      onChange={(value) => handleDateChange("customDateFrom", value)}
                      slots={{ openPickerIcon: CalendarMonthIcon }}
                      slotProps={{
                        textField: {
                          size: "small",
                          sx: { ...datePickerStyles, minWidth: 140 },
                          inputProps: { "data-testid": "perq-date-from" },
                        },
                      }}
                    />
                  </LocalizationProvider>
                  <Typography variant="body2" sx={viewTypographyStyles}>
                    through
                  </Typography>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      format="MM-DD-YYYY"
                      value={normalizeDayjsValue(filters.customDateTo)}
                      onChange={(value) => handleDateChange("customDateTo", value)}
                      slots={{ openPickerIcon: CalendarMonthIcon }}
                      slotProps={{
                        textField: {
                          size: "small",
                          sx: { ...datePickerStyles, minWidth: 140 },
                          inputProps: { "data-testid": "perq-date-to" },
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Box>
              ) : null}

              <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: "185px" } }}>
                <Select
                  data-testid="perq-restaurant-group"
                  value={filters.restaurant_group_id}
                  renderValue={() => getRestaurantGroupName(filters.restaurant_group_id)}
                  onChange={handleRestaurantGroupChange}
                  onClose={() => setTimeout(() => setRestaurantGroupSearch(""), 250)}
                  sx={selectBaseStyles}
                  endAdornment={
                    hasGroupSelected ? (
                      <InputAdornment position="end" sx={{ position: "absolute", right: 25 }}>
                        <IconButton edge="end" size="small" disableRipple onClick={handleRestaurantGroupClear}>
                          <ClearIcon sx={{ fontSize: "20px" }} />
                        </IconButton>
                      </InputAdornment>
                    ) : null
                  }
                  MenuProps={dropdownMenuProps(restaurantGroupMenuRef)}
                >
                  <DropdownSearchBar
                    changeSearchText={setRestaurantGroupSearch}
                    placeholder="Search restaurant group"
                    isFetching={restaurantGroupFetching}
                  />
                  {displayedRestaurantGroups.map((group) => (
                    <StyledMenuItem key={group.label} value={group.label}>
                      {filters.restaurant_group_id === group.label ? (
                        <SelectedMenuItem data={group.value} />
                      ) : (
                        group.value
                      )}
                    </StyledMenuItem>
                  ))}
                  <DropdownInfiniteLoader
                    hasNextPage={hasNextRestaurantGroupsPage}
                    targetRef={restaurantGroupLoaderRef}
                  />
                </Select>
              </FormControl>

              <FormControl
                size="small"
                sx={{ minWidth: { xs: "100%", sm: "175px" } }}
                disabled={!hasGroupSelected}
              >
                <Select
                  data-testid="perq-restaurant-sub-group"
                  value={filters.restaurant_sub_group_id}
                  renderValue={() => getSubGroupName(filters.restaurant_sub_group_id)}
                  onChange={handleSubGroupChange}
                  onClose={() => setTimeout(() => setRestaurantSubGroupSearch(""), 250)}
                  sx={selectBaseStyles}
                  endAdornment={
                    filters.restaurant_sub_group_id !== ALL_FILTER_VALUE ? (
                      <InputAdornment position="end" sx={{ position: "absolute", right: 25 }}>
                        <IconButton edge="end" size="small" disableRipple onClick={handleSubGroupClear}>
                          <ClearIcon sx={{ fontSize: "20px" }} />
                        </IconButton>
                      </InputAdornment>
                    ) : null
                  }
                  MenuProps={dropdownMenuProps(restaurantSubGroupMenuRef)}
                >
                  <DropdownSearchBar
                    changeSearchText={setRestaurantSubGroupSearch}
                    placeholder="Search sub-group"
                    isFetching={restaurantSubGroupFetching}
                  />
                  {displayedSubGroups.map((subGroup) => (
                    <StyledMenuItem key={subGroup.value} value={subGroup.value}>
                      {`${filters.restaurant_sub_group_id}` === `${subGroup.value}` ? (
                        <SelectedMenuItem data={subGroup.label} />
                      ) : (
                        subGroup.label
                      )}
                    </StyledMenuItem>
                  ))}
                  <DropdownInfiniteLoader
                    hasNextPage={hasNextRestaurantSubGroupsPage}
                    targetRef={restaurantSubGroupLoaderRef}
                  />
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: "195px" } }}>
                <Select
                  data-testid="perq-restaurant"
                  multiple
                  value={filters.restaurant_ids}
                  renderValue={() => getRestaurantDisplayValue()}
                  onChange={handleRestaurantChange}
                  onClose={() => setTimeout(() => setRestaurantSearch(""), 250)}
                  sx={selectBaseStyles}
                  endAdornment={
                    !filters.restaurant_ids.includes(ALL_FILTER_VALUE) ? (
                      <InputAdornment position="end" sx={{ position: "absolute", right: 25 }}>
                        <IconButton edge="end" size="small" disableRipple onClick={handleRestaurantClear}>
                          <ClearIcon sx={{ fontSize: "20px" }} />
                        </IconButton>
                      </InputAdornment>
                    ) : null
                  }
                  MenuProps={dropdownMenuProps(restaurantMenuRef)}
                >
                  <DropdownSearchBar
                    changeSearchText={setRestaurantSearch}
                    placeholder="Search restaurant"
                    isFetching={restaurantFetching}
                  />
                  {displayedRestaurants.map((restaurant) => (
                    <StyledMenuItem key={restaurant.id} value={restaurant.id}>
                      {filters.restaurant_ids.includes(restaurant.id) ? (
                        <SelectedMenuItem data={restaurant.name} />
                      ) : (
                        restaurant.name
                      )}
                    </StyledMenuItem>
                  ))}
                  <DropdownInfiniteLoader
                    hasNextPage={hasNextRestaurantsPage}
                    targetRef={restaurantLoaderRef}
                  />
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: "150px" } }}>
                <Select
                  data-testid="perq-order-type"
                  value={filters.order_type}
                  renderValue={() => ORDER_TYPE_LABELS[filters.order_type]}
                  onChange={handleOrderTypeChange}
                  sx={selectBaseStyles}
                  MenuProps={plainMenuProps}
                >
                  {PERQ_ORDER_TYPES.map((value) => (
                    <StyledMenuItem key={value} value={value}>
                      {ORDER_TYPE_LABELS[value]}
                    </StyledMenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: "160px" } }}>
                <Select
                  data-testid="perq-discounts"
                  multiple
                  value={filters.discounts}
                  renderValue={getDiscountsDisplayValue}
                  onChange={handleDiscountsChange}
                  displayEmpty
                  sx={selectBaseStyles}
                  MenuProps={plainMenuProps}
                >
                  {PERQ_DISCOUNT_CLASSES.map((value) => (
                    <StyledMenuItem key={value} value={value}>
                      {filters.discounts.includes(value) ? (
                        <SelectedMenuItem data={PERQ_DISCOUNT_CLASS_LABELS[value]} />
                      ) : (
                        PERQ_DISCOUNT_CLASS_LABELS[value]
                      )}
                    </StyledMenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: "195px" } }}>
                <Select
                  data-testid="perq-discount-names"
                  multiple
                  value={filters.discount_names}
                  renderValue={() => getDiscountNamesDisplayValue(filters.discount_names)}
                  onChange={handleDiscountNamesChange}
                  onOpen={() => setDiscountNameMenuOpen(true)}
                  onClose={() => {
                    setDiscountNameMenuOpen(false);
                    setTimeout(() => setDiscountNameSearch(""), 250);
                  }}
                  displayEmpty
                  sx={selectBaseStyles}
                  endAdornment={
                    filters.discount_names.length > 0 ? (
                      <InputAdornment position="end" sx={{ position: "absolute", right: 25 }}>
                        <IconButton
                          edge="end"
                          size="small"
                          disableRipple
                          aria-label="Clear discount names"
                          onClick={handleDiscountNamesClear}
                        >
                          <ClearIcon sx={{ fontSize: "20px" }} />
                        </IconButton>
                      </InputAdornment>
                    ) : null
                  }
                  MenuProps={dropdownMenuProps(discountNameMenuRef)}
                >
                  <DropdownSearchBar
                    changeSearchText={setDiscountNameSearch}
                    placeholder="Search discount name"
                    isFetching={discountNameFetching}
                  />
                  {displayedDiscountNames.map((option) => (
                    <StyledMenuItem key={option.value} value={option.value}>
                      {filters.discount_names.includes(option.value) ? (
                        <SelectedMenuItem data={renderDiscountNameOption(option)} />
                      ) : (
                        renderDiscountNameOption(option)
                      )}
                    </StyledMenuItem>
                  ))}
                  <DropdownInfiniteLoader
                    hasNextPage={hasNextDiscountNamesPage}
                    targetRef={discountNameLoaderRef}
                  />
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: "150px" } }}>
                <Select
                  data-testid="perq-check-in"
                  value={filters.check_in}
                  displayEmpty
                  renderValue={() =>
                    filters.check_in === ALL_FILTER_VALUE
                      ? "Check-in"
                      : CHECK_IN_LABELS[filters.check_in]
                  }
                  onChange={handleCheckInChange}
                  sx={selectBaseStyles}
                  endAdornment={
                    hasCheckInSelected ? (
                      <InputAdornment position="end" sx={{ position: "absolute", right: 25 }}>
                        <IconButton edge="end" size="small" disableRipple onClick={handleCheckInClear}>
                          <ClearIcon sx={{ fontSize: "20px" }} />
                        </IconButton>
                      </InputAdornment>
                    ) : null
                  }
                  MenuProps={plainMenuProps}
                >
                  {PERQ_CHECK_IN_OPTIONS.map((value) => (
                    <StyledMenuItem key={value} value={value}>
                      {filters.check_in === value ? (
                        <SelectedMenuItem data={CHECK_IN_LABELS[value]} />
                      ) : (
                        CHECK_IN_LABELS[value]
                      )}
                    </StyledMenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box
              sx={{
                display: "flex",
                gap: 1,
                alignItems: "center",
                flexWrap: "wrap",
                width: { xs: "100%", lg: "auto" },
              }}
            >
              <ResetButton onClick={handleReset} />
              <UpdateButton
                onClick={handleApplyFilters}
                filtersChanged={filtersChanged}
                status={isFetching}
                disabled={isUpdateDisabled}
              />
            </Box>
          </Box>
        </Box>

        <MenuHeading
          report={false}
          rightComponent={
            <StyledSearchBar
              value={filters.search}
              placeholder="Search order, employee, code, discount"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              onChange={handleSearchChange}
              autoComplete="off"
              inputProps={{ "data-testid": "perq-search" }}
            />
          }
        >
          Perq Report
        </MenuHeading>

        <Box sx={{ px: 2 }}>
          <PerqReportSummary
            isMobile={isMobile}
            data={summaryData}
            loading={summaryFetching && !summaryData}
            error={Boolean(summaryError)}
            selectedDrilldown={selectedSummaryCard}
            onCardClick={handleSummaryCardClick}
          />
          <PerqReportBreakdown appliedFilters={appliedFilters} />
        </Box>

        <Box sx={{ px: 2, pb: 2 }}>
          {showTableLoader ? (
            <Box
              data-testid="perq-table-loader"
              role="status"
              aria-live="polite"
              aria-label="Loading report results"
            >
              <Loader />
            </Box>
          ) : (
            <IndexTable
              report={false}
              columns={columns}
              data={rows}
              type="orders"
              tableId="perq-report"
              getRowProps={getPerqRowProps}
              itemsPerPage={itemsPerPage}
              setItemsPerPage={setItemsPerPage}
              page={page}
              setPage={setPage}
              pageCount={discountReports?.pageCount || 0}
              sortMode="server"
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSortChange={handleColumnSortChange}
              showDownloadButton={true}
              disableDownloadButton={rows.length === 0 || csvLoading}
              handleExportCsv={handleExportCsv}
              csvLoading={csvLoading}
            />
          )}
        </Box>
      </Box>

      <OrderAppliedDiscountModal
        open={orderViewOpen}
        handleClose={handleCloseOrderModal}
        ordersData={orderData}
        isLoading={orderDataLoading}
      />
    </React.Fragment>
  );
};
